import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChatCircleText } from "@phosphor-icons/react";

const ROLE_COLOR = {
  client: "text-[#a3a3a3] border-[#262626]",
  designer: "text-[#FF2D2D] border-[#FF2D2D]/30",
  admin: "text-white border-white/20",
};

const ROLE_LABEL = {
  client: "Client",
  designer: "Designer",
  admin: "Studio",
};

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch (_) {
    return "";
  }
}

export function AdminGlobalMessages() {
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/recent-messages");
        if (!cancel) {
          setMessages(data);
          setLoaded(true);
        }
      } catch (_) {
        if (!cancel) setLoaded(true);
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => {
      cancel = true;
      clearInterval(t);
    };
  }, []);

  return (
    <div className="mb-10" data-testid="admin-global-messages">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl uppercase">Recent conversations</h2>
        <span className="text-xs text-[#a3a3a3]">All projects · all roles</span>
      </div>

      <div className="border border-[#262626] bg-[#0a0a0a] divide-y divide-[#1a1a1a] max-h-[420px] overflow-y-auto">
        {!loaded ? (
          <div className="p-6 text-center text-[#a3a3a3] text-sm">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="p-8 text-center text-[#a3a3a3]">
            <ChatCircleText size={28} className="mx-auto mb-2" />
            <div className="text-sm">No messages yet across the studio.</div>
          </div>
        ) : (
          messages.map((m) => (
            <Link
              key={m.id}
              to={`/admin/projects/${m.project_id}`}
              data-testid={`global-msg-${m.id}`}
              className="block p-4 hover:bg-[#121212] transition-colors"
            >
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="font-semibold">{m.sender_name}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${ROLE_COLOR[m.sender_role] || ROLE_COLOR.client}`}>
                  {ROLE_LABEL[m.sender_role] || m.sender_role}
                </span>
                <span className="text-[#666]">·</span>
                <span className="text-[#a3a3a3] truncate">{m.project_title}</span>
                {m.is_internal && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF2D2D]/30 text-[#FF2D2D]">
                    Internal
                  </span>
                )}
                <span className="ml-auto text-[#666]">{timeAgo(m.created_at)}</span>
              </div>
              <div className="text-sm text-white mt-1.5 line-clamp-2 whitespace-pre-wrap">
                {m.body}
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
